import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useSession } from '../../../global/auth/useSession';
import { useApiUrl } from '../../../global/config/useApiUrl';

interface EditChannelDialogProps {
  open: boolean;
  conversationId: string;
  currentName: string;
  currentDescription?: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditChannelDialog = ({
  open,
  conversationId,
  currentName,
  currentDescription,
  onClose,
  onUpdated,
}: EditChannelDialogProps) => {
  const { user } = useSession();
  const apiUrl = useApiUrl();

  const [name, setName] = useState(currentName);
  const [description, setDescription] = useState(currentDescription || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setName(currentName);
    setDescription(currentDescription || '');
  }, [currentName, currentDescription, open]);

  const handleSubmit = async () => {
    if (!name.trim() || !user?.token) return;

    setSubmitting(true);
    try {
      await fetch(`${apiUrl}/vm/chat/channel/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${user.token}`,
        },
        body: JSON.stringify({
          conversationId,
          name: name.trim(),
          description: description.trim() || null,
        }),
      });
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Failed to update channel:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Grid container justify='space-between' alignItems='flex-start'>
          <Grid item>
            <Typography variant='h6'>Edit Channel</Typography>
          </Grid>
          <Grid item>
            <IconButton aria-label='Close' color='inherit' onClick={onClose}>
              <CloseIcon color='action' />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Channel Name'
              variant='outlined'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Description (optional)'
              variant='outlined'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              maxRows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display='flex' justifyContent='flex-end' width='100%'>
          <TabletButton variant='text' onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={!name.trim() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Save'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
